<template>
  <v-container fluid class="pt-2">
    <v-row align="center" class="px-2 pb-1">
      <h2 class="subtitle-1 font-weight-medium">Luggage Transfers</h2>
      <v-spacer></v-spacer>
      <v-btn
        small
        text
        color="primary"
        class="mr-2"
        @click="showApplyBags = true"
        :disabled="legs.length === 0"
      >
        <v-icon small left>mdi-bag-suitcase</v-icon>Set bags for all
      </v-btn>
      <v-btn
        small
        text
        color="primary"
        :loading="csvLoading"
        :disabled="legs.length === 0"
        @click="getCsv"
      >
        <v-icon small left>mdi-download</v-icon>Get CSV
      </v-btn>
    </v-row>

    <v-data-table
      class="elevation-1"
      :headers="headers"
      :items="legs"
      :loading="$apollo.loading"
      :items-per-page="itemsPerPage"
      :footer-props="{ itemsPerPageOptions: [10, 25, 50, -1] }"
      dense
    >
      <template v-slot:[`item.date`]="{ item }">
        <v-icon
          v-if="item.gapWarning"
          small
          color="warning"
          class="mr-1"
          title="Gap night / pickup hotel not matched — please check"
          >mdi-alert</v-icon
        >
        {{ formatDate(item.date) }}
      </template>

      <template v-slot:[`item.pickup`]="{ item }">
        <div>{{ item.pickupLocation || "—" }}</div>
        <div class="grey--text caption" v-if="item.pickupAddress">
          {{ item.pickupAddress }}
        </div>
      </template>

      <template v-slot:[`item.dropoffDate`]="{ item }">
        {{ formatDate(item.date) }}
      </template>

      <template v-slot:[`item.dropoff`]="{ item }">
        <div>{{ item.dropoffLocation || "—" }}</div>
        <div class="grey--text caption" v-if="item.dropoffAddress">
          {{ item.dropoffAddress }}
        </div>
      </template>

      <template v-slot:[`item.participants`]="{ item }">
        <div class="d-flex align-center">
          <span class="mr-1">{{
            (item.assignedParticipants || []).length
          }}</span>
          <v-btn icon x-small @click="openParticipants(item)">
            <v-icon x-small>mdi-pencil</v-icon>
          </v-btn>
        </div>
      </template>

      <template v-slot:[`item.bags`]="{ item }">
        <v-text-field
          type="number"
          min="0"
          :value="item.bags"
          hide-details
          dense
          single-line
          style="max-width: 70px"
          @change="(v) => onBagsChange(item, v)"
        ></v-text-field>
      </template>

      <template v-slot:[`item.included`]="{ item }">
        <v-checkbox
          :input-value="item.included"
          hide-details
          dense
          class="mt-0 pt-0"
          @change="(v) => onIncludedChange(item, v)"
        ></v-checkbox>
      </template>
    </v-data-table>

    <!-- Set bags for all legs -->
    <v-dialog v-model="showApplyBags" max-width="320">
      <v-card>
        <v-card-title class="subtitle-1">Set bags for all legs</v-card-title>
        <v-card-text>
          <v-text-field
            type="number"
            min="0"
            label="Bags"
            v-model.number="applyBagsValue"
            autofocus
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showApplyBags = false">Cancel</v-btn>
          <v-btn color="primary" text :loading="saving" @click="applyBagsToAll"
            >Apply</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit assigned participants -->
    <v-dialog v-model="showParticipants" max-width="480">
      <v-card v-if="editingLeg">
        <v-card-title class="subtitle-1">Assigned participants</v-card-title>
        <v-card-text>
          <v-combobox
            v-model="editParticipants"
            :items="allParticipantNames"
            multiple
            chips
            deletable-chips
            small-chips
            hide-details
            label="Participants"
          ></v-combobox>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showParticipants = false">Cancel</v-btn>
          <v-btn color="primary" text :loading="saving" @click="saveParticipants"
            >Save</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import {
  ITINERARY_LUGGAGE_TRANSFERS,
  UPDATE_LUGGAGE_TRANSFER,
  GENERATE_LUGGAGE_TRANSFERS_CSV,
} from "../../../constants/graphql";
import { removeGQLTypename } from "../../../helpers/index";
const moment = require("moment");

export default {
  name: "luggage-transfers-tab",

  props: {
    itinerary: { type: Object, required: true },
  },

  data() {
    return {
      luggageTransfers: [],
      itemsPerPage: 10,
      saving: false,
      csvLoading: false,
      showApplyBags: false,
      applyBagsValue: 0,
      showParticipants: false,
      editingLeg: null,
      editParticipants: [],
      headers: [
        { text: "Pick up date", value: "date" },
        { text: "Pick up", value: "pickup", sortable: false },
        { text: "Pick up tel", value: "pickupTel", sortable: false },
        { text: "Drop off date", value: "dropoffDate", sortable: false },
        { text: "Drop off", value: "dropoff", sortable: false },
        { text: "Drop off tel", value: "dropoffTel", sortable: false },
        { text: "Participants", value: "participants", sortable: false },
        { text: "Bags", value: "bags", sortable: false },
        { text: "Included", value: "included", sortable: false },
      ],
    };
  },

  computed: {
    legs() {
      return (this.luggageTransfers || []).map(removeGQLTypename);
    },
    allParticipantNames() {
      const edges =
        this.itinerary &&
        this.itinerary.participants &&
        this.itinerary.participants.edges
          ? this.itinerary.participants.edges
          : [];
      return edges
        .map((e) => e.node)
        .map((p) => [p.name, p.surname].filter(Boolean).join(" ").trim())
        .filter(Boolean);
    },
  },

  methods: {
    formatDate(d) {
      if (!d) return "";
      return moment(d).format(this.$store.getters.userConfig.dateFormat);
    },
    refetch() {
      this.$apollo.queries.luggageTransfers.refetch();
    },
    update(variables, successMsg) {
      this.saving = true;
      return this.$apollo
        .mutate({ mutation: UPDATE_LUGGAGE_TRANSFER, variables })
        .then((res) => {
          const r = res.data.updateLuggageTransfer;
          if (r && r.success) {
            if (successMsg) {
              this.$store.dispatch("showAppSnackbar", {
                msg: successMsg,
                color: "success",
              });
            }
            this.refetch();
          } else {
            this.$store.dispatch("showAppSnackbar", {
              msg: (r && r.error) || "Update failed",
              color: "error",
            });
          }
        })
        .catch((err) => {
          this.$store.dispatch("showAppSnackbar", {
            msg: err.message || String(err),
            color: "error",
          });
        })
        .finally(() => {
          this.saving = false;
        });
    },
    onBagsChange(item, value) {
      const bags = parseInt(value, 10);
      if (isNaN(bags) || bags < 0) return;
      this.update({ id: item.id, bags });
    },
    onIncludedChange(item, value) {
      this.update({ id: item.id, included: !!value });
    },
    applyBagsToAll() {
      const bags = parseInt(this.applyBagsValue, 10);
      if (isNaN(bags) || bags < 0 || this.legs.length === 0) return;
      this.update(
        { id: this.legs[0].id, bags, applyToAll: true },
        "Bags applied to all legs"
      ).then(() => {
        this.showApplyBags = false;
      });
    },
    openParticipants(item) {
      this.editingLeg = item;
      this.editParticipants = [...(item.assignedParticipants || [])];
      this.showParticipants = true;
    },
    saveParticipants() {
      this.update(
        {
          id: this.editingLeg.id,
          assignedParticipants: this.editParticipants,
        },
        "Participants updated"
      ).then(() => {
        this.showParticipants = false;
      });
    },
    getCsv() {
      this.csvLoading = true;
      this.$apollo
        .mutate({
          mutation: GENERATE_LUGGAGE_TRANSFERS_CSV,
          variables: { itineraryId: this.itinerary.id },
        })
        .then((res) => {
          const r = res.data.generateLuggageTransfersCsv;
          if (r && r.success) {
            window.open(r.message);
          } else {
            this.$store.dispatch("showAppSnackbar", {
              msg: (r && r.error) || "CSV generation failed",
              color: "error",
            });
          }
        })
        .catch((err) => {
          this.$store.dispatch("showAppSnackbar", {
            msg: err.message || String(err),
            color: "error",
          });
        })
        .finally(() => {
          this.csvLoading = false;
        });
    },
  },

  apollo: {
    luggageTransfers: {
      query: ITINERARY_LUGGAGE_TRANSFERS,
      variables() {
        return { id: this.itinerary.id };
      },
      update: (data) =>
        data.itinerary ? data.itinerary.luggageTransfers : [],
      fetchPolicy: "network-only",
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/gt/itineraries/LuggageTransfersTab.vue